/**
 * La red de envíos de Smarty para cualquier venta propia (20261028150000), como Mercado Envíos.
 *
 * El founder (28-oct-2026) creó desde Chats dos ventas propias de Vitalex y MERCourier no apareció: la pantalla le
 * preguntaba a la base por los couriers de Smarty sólo si la venta era de Drop. Ahora una venta propia ve, junto a sus
 * transportistas, los couriers de la red que Smarty abrió para su negocio, con el envío que se le cobra (tarifa del
 * courier + el recargo de Smarty) o el motivo por el que no se puede.
 *
 * ⭐ TODO LO DECIDE LA BASE: quién está abierto para quién (`fn_red_esta_abierta_para`), si la dirección alcanza
 *    (distrito del ubigeo y pin), de dónde sale y cuánto cuesta. La pantalla sólo pinta y elige.
 * 🔒 El negocio no recibe la tarifa del courier: `tarifa_del_courier` y `recargo` sólo le llegan a Smarty.
 */
import { supabase } from '@/lib/supabase';
import type { AvailableCourierOption } from '@/lib/logistics-resolver';
import { MARCA_DE_ZONA_ROJA } from '@/lib/logistics/laZonaRojaDelCourier';

/** Por qué un courier de la red no se puede elegir (lo dice la base). */
export type MotivoDeLaRed =
  | 'no_es_contra_entrega' | 'falta_el_distrito' | 'falta_el_pin' | 'sin_almacen' | 'no_recoge_ahi' | 'fuera_de_cobertura';

export interface OpcionDeLaRed {
  courier_id: string;
  courier: string;
  logo_url: string | null;
  zona: string | null;
  /** Lo que se le cobra al negocio por el envío (tarifa del courier + recargo de Smarty); null si no se sabe. */
  envio: number | null;
  cubre: boolean;
  motivo: MotivoDeLaRed | null;
  /** El motivo en palabras de quien tiene que hacer algo. */
  por_que: string | null;
  /** De dónde sale el paquete: el almacén del negocio, o el del courier si guarda su mercadería. */
  origen: string | null;
  /** Sólo para Smarty. */
  tarifa_del_courier: number | null;
  recargo: number | null;
  /** La zona roja del courier donde cae el pin (20261029010000): se elige, pero entrega coordinando. */
  coordinar?: string | null;
}

const num = (v: unknown): number | null => (v === null || v === undefined || v === '' ? null : Number(v));

export async function opcionesDeLaRed(ventaId: string): Promise<OpcionDeLaRed[]> {
  const { data, error } = await supabase.rpc('fn_red_opciones_de_la_venta', { p_venta: ventaId });
  if (error) throw error;
  return ((data ?? []) as OpcionDeLaRed[]).map((o) => ({
    ...o, envio: num(o.envio), tarifa_del_courier: num(o.tarifa_del_courier), recargo: num(o.recargo),
  }));
}

/** El negocio elige un courier de la red para su venta: la base valida la dirección, fija de dónde sale y lo asigna. */
export async function enviarPorLaRed(ventaId: string, courierId: string): Promise<void> {
  const { error } = await supabase.rpc('fn_red_enviar_la_venta', { p_venta: ventaId, p_courier: courierId });
  if (error) throw error;
}

/** La venta vuelve a un transportista propio: deja de llevar al courier de la red (si lo tenía). */
export async function quitarDeLaRed(ventaId: string): Promise<void> {
  const { error } = await supabase.rpc('fn_red_quitar_de_la_venta', { p_venta: ventaId });
  if (error) throw error;
}

/** Hasta dónde llega un courier de la red: nadie, los negocios de Drop, o todos los negocios. Lo decide Smarty. */
export type AlcanceDeLaRed = 'apagado' | 'drop' | 'todos';

/** Si Smarty lo abrió a todos los negocios (lo lee Smarty en la ficha del courier). */
export async function estaAbiertoATodos(catalogoId: string): Promise<boolean> {
  const { data, error } = await supabase.from('logistics_couriers').select('abierto_a_todos').eq('id', catalogoId).maybeSingle();
  if (error) throw error;
  return !!(data as { abierto_a_todos?: boolean } | null)?.abierto_a_todos;
}

/** Smarty cambia el alcance de un courier (la base exige que sea Smarty y reusa el interruptor de Drop). */
export async function cambiarAlcanceDeLaRed(catalogoId: string, alcance: AlcanceDeLaRed): Promise<void> {
  const { error } = await supabase.rpc('fn_red_alcance_del_courier', { p_catalogo: catalogoId, p_alcance: alcance });
  if (error) throw error;
}

/** El prefijo de las opciones del selector que son couriers de la red: al elegirlas se ASIGNAN, no se escriben. */
export const OPCION_DE_LA_RED = 'smarty-red:';

export const esOpcionDeLaRed = (o: Pick<AvailableCourierOption, 'courier_service_id'> | null | undefined): boolean =>
  !!o?.courier_service_id.startsWith(OPCION_DE_LA_RED);

const soles = (n: number) => `S/ ${n.toFixed(2)}`;

/**
 * Los couriers de la red como opciones del selector de envío, después de los del negocio. Los que no se pueden elegir
 * se ven deshabilitados con su motivo (y el envío, si ya se sabe): «Falta el pin del cliente en el mapa · S/ 13.00».
 */
export function laRedComoOpciones(opciones: readonly OpcionDeLaRed[]): AvailableCourierOption[] {
  return opciones.map((o, i) => ({
    courier_service_id: `${OPCION_DE_LA_RED}${o.courier_id}`,
    courier_id: o.courier_id,
    courier_name: o.courier,
    courier_type: 'motorizado',
    service_name: o.cubre
      ? ['Red de Smarty', o.envio !== null ? soles(o.envio) : null, o.zona ? `zona ${o.zona}` : null,
          o.coordinar ? MARCA_DE_ZONA_ROJA : null].filter(Boolean).join(' · ')
      : [o.por_que ?? 'No disponible', o.envio !== null ? `desde ${soles(o.envio)}` : null].filter(Boolean).join(' · '),
    delivery_type: 'home',
    priority: 200 + i,
    is_auto_assign: false,
    logo_url: o.logo_url,
    brand_color: null,
    disabled: !o.cubre,
    envio_de_la_red: o.envio,
    coordinar: o.cubre ? o.coordinar ?? null : null,
  }));
}
